import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

export interface ParsedAddress {
  streetAddress: string;
  city: string;
  zip: string;
  state: string;
}

let apiInitialized = false;
export function initGoogleApi() {
  if (apiInitialized) return;
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  if (!apiKey) throw new Error('VITE_GOOGLE_MAPS_API_KEY is not set');
  setOptions({ key: apiKey });
  apiInitialized = true;
}

// Geocoding API uses long_name / short_name (classic types)
function extractFromGeocoder(
  components: google.maps.GeocoderAddressComponent[]
): ParsedAddress | null {
  const get = (type: string, name: 'long_name' | 'short_name' = 'long_name') =>
    components.find(c => c.types.includes(type))?.[name] ?? '';
  const streetAddress = [get('street_number'), get('route')].filter(Boolean).join(' ');
  const city = get('locality') || get('sublocality_level_1') || get('neighborhood');
  const zip = get('postal_code');
  const state = get('administrative_area_level_1', 'short_name');
  if (!streetAddress || !city || !zip || !state) return null;
  return { streetAddress, city, zip, state };
}

/**
 * Validate/normalize a raw address string via the Geocoding API.
 * Used on submit when the user typed manually without selecting a suggestion.
 */
export async function geocodeAddress(raw: string): Promise<ParsedAddress | null> {
  try {
    initGoogleApi();
    const geocodingLib = await importLibrary('geocoding') as google.maps.GeocodingLibrary;
    const geocoder = new geocodingLib.Geocoder();
    return await new Promise((resolve) => {
      geocoder.geocode({ address: raw }, (results, status) => {
        if (status !== 'OK' || !results?.length) {
          console.warn('[geocodeAddress] Geocoder status:', status);
          resolve(null);
          return;
        }
        resolve(extractFromGeocoder(results[0].address_components));
      });
    });
  } catch (err) {
    console.warn('[geocodeAddress] Geocoder error:', err);
    return null;
  }
}
